import PropTypes from 'prop-types';
import React, {memo, useEffect, useMemo, useRef, useState} from 'react';
import JoditEditor from 'jodit-react';
import {FmlxButton, FmlxIcon} from 'fmlx-common-ui';
import {FMLX_BUTTON} from '../../../const/FmlxComponentEnum';
import './CategoryDescription.scss';
import useEditorState from '../../../hooks/useEditorState';
import GeneralDescriptionContent from '../../../api/rest/GeneralQuestionDescriptionApi';
import {generateMarkupFromDraftjs} from '../../../lib/utilities';
import {useManageQuestionContext} from '../../../lib/contexLib';
import skillCategoryApi from '../../../api/rest/skillCategoryApi';
import {useSelector, useDispatch} from 'react-redux'; // BENAR: Import useSelector dan useDispatch
import {selectPeriodeAndAuthRole} from '../../../stores/selectors/appStateSelector'; // BENAR: Import selector
import {ALERT_VARIANT} from '../../../const/dataEnum';
import {setAlert} from '../../../stores/actions/alertModalStateAction';

const Config = {
  readonly: false,
  formatHTML: true,
  placeholder: 'Start typing...',
  buttons: [
    'undo',
    'redo',
    '|',
    'bold',
    'strikethrough',
    'underline',
    'italic',
    '|',
    'align',
    '|',
    'ul',
    'ol',
    'outdent',
    'indent',
    '|',
    'font',
    'fontsize',
    '|',
    'link',
    'table',
    '|',
    'hr',
  ],
};

const DefaultDescription = `
  <div>
    <div>
      <div>Scale 1-5</div>
      <div>1 = Poor (Disciplinary Action Required)</div>
      <div>3 = Average (Meets Expectations)</div>
      <div>5 = Stellar (Significantly Exceeds Expectations)</div>
    </div>
    <div>
      <div>Scale 1-10</div>
      <div>1 = Poor (Disciplinary Action Required)</div>
      <div>5 = Average (Meets Expectations)</div>
      <div>10 = Stellar (Significantly Exceeds Expectations)</div>
    </div>
  </div>`;

const MemoizedActionButtons = React.memo(({onSave, onCancel}) => (
  <div className="manage-question--category-description--action">
    <FmlxButton label="Cancel" variant={FMLX_BUTTON.VARIANT.PLAIN} type={FMLX_BUTTON.TYPE.BASIC} onClick={onCancel} />
    <FmlxButton label="Save" onClick={onSave} />
  </div>
));

const MemoizedEditButton = React.memo(({onClick}) => (
  <FmlxButton
    onlyIcon
    icon={<FmlxIcon name="Pencil" />}
    variant={FMLX_BUTTON.VARIANT.PLAIN}
    type={FMLX_BUTTON.TYPE.BASIC}
    tooltipDescription="Edit Description"
    onClick={onClick}
  />
));

const MemoizedDescriptionView = React.memo(({description}) => (
  <div className="manage-question--category-description--skill-description">
    <div dangerouslySetInnerHTML={generateMarkupFromDraftjs(description)} />
  </div>
));

const CategoryDescription = ({isGeneral, fetchTabs}) => {
  const {activeTab, tabs, selectedDepartment } = useManageQuestionContext();
  const { currentPeriodId } = useSelector(selectPeriodeAndAuthRole);
  const dispatch = useDispatch();
  const selectedSkillCategory = tabs.find((tab) => tab.id === activeTab?.id);
  const editorRef = useRef(null);

  const {editorState, isEditing, startEditing, cancelEditing, setEditorState} = useEditorState();

  const memoizedConfig = useMemo(() => Config, []);

  const handleOnBlurEditorStateChange = (data) => setEditorState(data);

  const [generalDescription, setGeneralDescription] = useState(null);
  const [generalDescriptionId, setGeneralDescriptionId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

   useEffect(() => {
    const fetchGeneralDescription = async () => {
      if (!isGeneral || !selectedDepartment?.id || !currentPeriodId) return;
      setIsLoading(true);

      const response = await GeneralDescriptionContent.getDescContentByDepartementId(selectedDepartment.id, currentPeriodId);

      if (response?.responseCode === 0 && response.data && response.data.length > 0) {
        const descriptionForCurrentPeriod = response.data.find(desc => desc.reviewPeriodID === currentPeriodId);
        
        if (descriptionForCurrentPeriod) {
          const {id, description} = descriptionForCurrentPeriod; 
          setGeneralDescription(description || DefaultDescription);
          setGeneralDescriptionId(id || null);
        } else {
          setGeneralDescription(DefaultDescription);
          setGeneralDescriptionId(null);
        }
        
      } 
      setIsLoading(false);
    };

    fetchGeneralDescription();
  }, [isGeneral, isEditing, selectedDepartment?.id, currentPeriodId]);

  const onSaveDescriptionClickHandler = async () => {
    const basePayload = {
      description: editorState.replace(/(\r\n|\n|\r)/gm, ''),
      departmentID: selectedDepartment.id,
      reviewPeriodID: currentPeriodId, 
    };

    if (editorState.trim() === (generalDescription || '').trim()) {
      dispatch(setAlert({ show: true, variant: ALERT_VARIANT.INFO, message: 'No changes detected' }));
      cancelEditing();
      return;
    }

    try {
      setIsLoading(true);
      if (isGeneral) {
        const payload = {
          id: generalDescriptionId || 0,
          ...basePayload,
        };
        const response = await GeneralDescriptionContent.updateDescriptionContent(payload);
        
        if (response) {
          dispatch(setAlert({show: true, variant: ALERT_VARIANT.SUCCESS, message: response.message}));
        } else {
          dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: 'Failed to save description.'}));
        }

      } else {
        const skillPayload = {
          ...activeTab,
          description: editorState,
          isDeleted: false,
        };
        const response = await skillCategoryApi.update(skillPayload);
        if (response && response.responseCode === 0) {
          dispatch(setAlert({show: true, variant: ALERT_VARIANT.SUCCESS, message: response.message}));
          await fetchTabs();
        } else {
          dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: response?.message || 'Failed to update category description.'}));
        }
      }
    } catch (error) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: error.message}));
    } finally {
      setIsLoading(false);
      cancelEditing(); 
    }
  };

  const handleEditClick = () => {
    if (isGeneral) {
      setEditorState(generalDescription || '');
    } else {
      const initialContent = selectedSkillCategory?.description || '';
      setEditorState(initialContent);
    }
    startEditing();
  };


  const getDescriptionContent = () => {
    if (isGeneral) {
      if (isLoading) {
        return <div>Loading...</div>;
      }

      return (
        <>
          {isEditing ? (
            <JoditEditor
              ref={editorRef}
              value={generalDescription}
              config={memoizedConfig}
              tabIndex={1}
              onBlur={handleOnBlurEditorStateChange}
            />
          ) : (
            <MemoizedDescriptionView description={generalDescription || DefaultDescription} />
          )}
        </>
      );
    }

    if (isEditing) {
      return (
        <JoditEditor
          ref={editorRef}
          value={editorState}
          config={memoizedConfig}
          tabIndex={1}
          onBlur={handleOnBlurEditorStateChange}
        />
      );
    }

    return <MemoizedDescriptionView description={selectedSkillCategory?.description} />;
  };

  return (
    <div className="manage-question--category-description">
      <div className="manage-qestion--category-description--theme-bar"></div>
      <div className="manage-question--category-description--header">
        <div>{isGeneral ? 'General Questions' : `${selectedSkillCategory?.category} Questions`}</div>
        {isEditing ? (
          <MemoizedActionButtons onSave={onSaveDescriptionClickHandler} onCancel={cancelEditing} />
        ) : (
          <MemoizedEditButton onClick={handleEditClick} />
        )}
      </div>
      <div className="manage-question--category-description--content">{getDescriptionContent()}</div>
    </div>
  );
};

CategoryDescription.propTypes = {
  category: PropTypes.node,
  isGeneral: PropTypes.bool,
};

CategoryDescription.defaultProps = {
  category: null,
  isGeneral: false,
};

export default memo(CategoryDescription);
